import React, {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  useRef,
} from "react";
import "./search.css";
import * as FcIcons from "react-icons/fc";
import {
  FaBuilding,
  FaCalendarAlt,
  FaClipboardList,
  FaEnvelope,
  FaGlobeAmericas,
  FaMapMarkerAlt,
  FaPhone,
  FaCog,
  FaSearch,
  FaSlidersH,
  FaUser,
  FaUserSlash,
  FaUserTie,
  FaVenusMars,
} from "react-icons/fa";
import CustomColumnsModel from "./CustomColumnsModel";
import Multiselect from "multiselect-react-dropdown";
import Select, { components } from "react-select";
import CmsFilterMultiSelect from "./CmsFilterMultiSelect";
import CmsFilterSingleSelect from "./CmsFilterSingleSelect";

const PremiumDenseContext = createContext(false);

const PremiumIconField = ({ icon, children, hasSelection = false }) => {
  const dense = useContext(PremiumDenseContext);
  const heightClass = dense ? "h-[32px]" : "h-[38px]";
  const iconWidthClass = dense ? "w-[32px]" : "w-[38px]";
  return (
  <div
    className={
      hasSelection
        ? `flex ${heightClass} w-full cursor-pointer items-center overflow-visible rounded-md border border-blue-500/35 bg-white shadow-sm ring-1 ring-blue-500/10 transition focus-within:z-[300] focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20`
        : `flex ${heightClass} w-full cursor-pointer items-center overflow-visible rounded-md border border-gray-200 bg-white shadow-sm transition focus-within:z-[300] focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20`
    }
  >
    <span
      className={
        hasSelection
          ? `flex h-full ${iconWidthClass} shrink-0 items-center justify-center border-r border-blue-500/10 bg-blue-500/[0.14] text-blue-600 [&_svg]:block`
          : `flex h-full ${iconWidthClass} shrink-0 items-center justify-center border-r border-blue-500/10 bg-blue-500/10 text-blue-600 [&_svg]:block`
      }
      aria-hidden="true"
    >
      {icon}
    </span>
    {children}
  </div>
  );
};

const PremiumFieldWrap = ({ premium, label, icon, children, hasSelection }) => {
  const dense = useContext(PremiumDenseContext);
  if (!premium) return children;
  return (
    <div className="min-w-0 w-full">
      {label && (
        <label
          className={`${dense ? "mb-0.5" : "mb-1.5"} block text-[10px] font-medium text-slate-500 sm:text-[11px]`}
        >
          {label}
        </label>
      )}
      {icon ? (
        <PremiumIconField icon={icon} hasSelection={hasSelection}>
          {children}
        </PremiumIconField>
      ) : (
        children
      )}
    </div>
  );
};

const premiumColClass = (premium) => {
  if (!premium) return "col-lg-2 col-6";
  return "cms-filter-field min-w-0 w-full";
};

const defaultPremiumLabels = {
  input1: "Client name or ID",
  input2: "Email",
  input3: "Phone",
  input4: "Country",
  input5: "Gender",
  input6: "Processing Branch",
  input7: "Referring Branch",
  input8: "Assigned To",
  input9: "Created By",
  input11: "Event",
  input12: "Branch",
  input10: "Location",
  input13: "Intake",
};

const defaultPremiumIcons = {
  input1: <FaSearch size={12} />,
  input2: <FaEnvelope size={12} />,
  input3: <FaPhone size={12} />,
  input4: <FaGlobeAmericas size={12} />,
  input5: <FaVenusMars size={12} />,
  input6: <FaBuilding size={12} />,
  input7: <FaBuilding size={12} />,
  input8: <FaUser size={12} />,
  input9: <FaUserTie size={12} />,
  input11: <FaCalendarAlt size={12} />,
  input12: <FaBuilding size={12} />,
  input10: <FaMapMarkerAlt size={12} />,
  input13: <FaCalendarAlt size={12} />,
};

const getPremiumLabel = (premiumLabels, key) =>
  premiumLabels?.[key] || defaultPremiumLabels[key];

const getPremiumIcon = (premiumIcons, key) =>
  premiumIcons?.[key] || defaultPremiumIcons[key];

const premiumMsClass = (premium) =>
  premium
    ? "chip-hide cms-ms-premium h-full min-w-0 w-full flex-1 border-0 bg-transparent"
    : "form-select s-dropdown py-1 shadow-sm chip-hide mt-1 w-lg-90";

const PremiumMultiselect = ({
  premium,
  selectedValues,
  displayValue,
  className,
  hidePlaceholder,
  onSelect,
  onRemove,
  ...rest
}) => {
  const handleSelect = (selectedList, selectedItem) => {
    onSelect?.(selectedList, selectedItem);
  };
  const handleRemove = (selectedList, removedItem) => {
    if (onRemove) onRemove(selectedList, removedItem);
    else onSelect?.(selectedList, removedItem);
  };

  const multiselectProps = {
    className,
    selectedValues,
    displayValue,
    hidePlaceholder,
    onSelect: handleSelect,
    onRemove: handleRemove,
    ...rest,
  };

  if (!premium) {
    return <Multiselect {...multiselectProps} />;
  }

  return (
    <div className="relative flex h-full min-w-0 w-full flex-1 items-center">
      <Multiselect {...multiselectProps} />
    </div>
  );
};

const premiumSelectClass = (premium) =>
  `s-dropdown search py-0 shadow-sm chip-hide${
    premium ? " chip-hide h-full min-w-0 w-full flex-1" : " mt-1 w-lg-90"
  }`;

const PREMIUM_NATIVE_SELECT_CLASS =
  "h-full min-w-0 w-full flex-1 cursor-pointer appearance-none border-0 bg-transparent bg-[length:10px] bg-[position:right_0.65rem_center] bg-no-repeat py-0 pl-3 pr-8 text-xs font-medium text-slate-800 outline-none bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2210%22%20height%3D%2210%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%2394a3b8%22%20d%3D%22M3%204.5L6%207.5L9%204.5%22/%3E%3C/svg%3E')]";

const openDatePicker = (e) => {
  const input = e.currentTarget.querySelector('input[type="date"]');
  if (!input || input.disabled) return;
  if (typeof input.showPicker === "function") {
    try {
      input.showPicker();
    } catch {
      input.focus();
    }
  } else {
    input.focus();
  }
};

const sortByAgencyName = (data) =>
  data?.slice().sort((a, b) => (a.agencyname || "").localeCompare(b.agencyname || "")) ??
  [];

const sortByTitle = (data) =>
  data?.slice().sort((a, b) => (a.title || "").localeCompare(b.title || "")) ?? [];

const sortByName = (data) =>
  data?.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")) ?? [];

const sortByLabel = (data) =>
  data
    ?.slice()
    .sort((a, b) =>
      (a.label || a.name || a.title || "").localeCompare(
        b.label || b.name || b.title || "",
      ),
    ) ?? [];

const PremiumDateField = ({ label, name, value, min, disabled, onChange }) => (
  <div className="min-w-0 w-full">
    <label className="mb-1.5 block text-[11px] font-medium text-slate-500">{label}</label>
    <div
      className="flex h-[38px] w-full cursor-pointer items-center overflow-visible rounded-lg border border-gray-200 bg-white shadow-sm transition focus-within:z-[300] focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20"
      onClick={openDatePicker}
      onKeyDown={(e) => e.key === "Enter" && openDatePicker(e)}
      role="button"
      tabIndex={0}
    >
      <span
        className="flex h-full w-[38px] shrink-0 items-center justify-center border-r border-blue-500/10 bg-blue-500/10 text-blue-600 [&_svg]:block"
        aria-hidden="true"
      >
        <FaCalendarAlt size={12} />
      </span>
      <input
        type="date"
        name={name}
        className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-xs outline-none"
        value={value || ""}
        min={min}
        disabled={disabled}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  </div>
);

const MultipleSearchs = ({
  external_agent,
  externalAgentData,
  onSearchChange,
  allColumns,
  onColumnsChange,
  showDuplicateEntry,
  duplicate,
  input1_type,
  input1,
  input1_multiple,
  input1Data,
  input1_suggestion,
  input1_filter_by,
  input2_type,
  input2,
  input2_multiple,
  input2Data,
  input2_suggestion,
  input2_filter_by,
  input3_type,
  input3,
  input4_type,
  input3_multiple,
  input3Data,
  input3_filter_by,
  input4,
  input4_multiple,
  input4_filter_by,
  input4Data,
  input5_type,
  input5,
  input5_multiple,
  input5Data,
  input5_filter_by,
  input6_type,
  input6,
  input6Data,
  input6_multiple,
  input6_filter_by,
  input6_suggestion,
  input7_type,
  input7,
  input7Data,
  input7_multiple,
  input7_filter_by,
  input7_suggestion,
  input8_type,
  input8,
  input8Data,
  input8_multiple,
  input8_filter_by,
  input8_suggestion,
  input9_type,
  input9,
  input9_filter_by,
  input9Data,
  input9_multiple,
  input9_suggestion,
  input10_type,
  input10,
  input10Data,
  input10_multiple,
  input10_filter_by,
  input11_type,
  input11,
  input11Data,
  input11_multiple,
  input11_filter_by,
  input12_type,
  input12,
  input12Data,
  input12_multiple,
  input12_filter_by,
  input13_type,
  input13,
  input13Data,
  input13_multiple,
  input14_type,
  input14,
  input14Data,
  input14_multiple,
  input6CallBack,
  showSignupDateFilter,
  signupDateFilterAfterInput7,
  signupDateFilterAfterInput9,
  signupStartDate,
  signupEndDate,
  onSignupDateChange,
  showCreatedAtDateFilter,
  createdAtStartDate,
  createdAtEndDate,
  onCreatedAtDateChange,
  premium = false,
  premiumLabels = {},
  premiumIcons = {},
  signupDateLabels = {
    start: "Contract Start Date",
    end: "Contract End Date",
  },
  createdAtDateLabels = {
    start: "Created At Start Date",
    end: "Created At End Date",
  },
  hideSearchButton = false,
  hideClearAll = false,
  hideTopToolbar = false,
  moreFiltersOpen = false,
  searchRef,
  columnsSettingsRef,
  clearFiltersRef,
  showColumnsAfterSearch = false,
  hideFilterActionsRow = false,
  premiumDense = false,
  /** When set (e.g. 2), caps the premium filter grid to that many columns at all breakpoints. */
  premiumFixedColumns = 0,
  /** When parent filter state changes (e.g. chip remove), sync into local dropdown state. */
  filterArraySync = null,
  filterScalarSync = null,
}) => {
  const [openCustomColumnsModel, setOpenCustomColumnsModel] = useState(false);
  const [input2Type, setInput2Type] = useState("text");
  const [input3Type, setInput3Type] = useState("text");
  const [input4Type, setInput4Type] = useState("text");
  const [input5Type, setInput5Type] = useState("text");
  const [input1Value, setInput1Value] = useState("");
  const [input2Value, setInput2Value] = useState("");
  const [input3Value, setInput3Value] = useState("");
  const [input4Value, setInput4Value] = useState("");
  const [input5Value, setInput5Value] = useState("");
  const [input6Value, setInput6Value] = useState("");
  const [input7Value, setInput7Value] = useState("");
  const [input8Value, setInput8Value] = useState("");
  const [input9Value, setInput9Value] = useState("");
  const [input10Value, setInput10Value] = useState("");
  const [input11Value, setInput11Value] = useState("");
  const [input12Value, setInput12Value] = useState("");
  const [input13Value, setInput13Value] = useState("");
  const [input14Value, setInput14Value] = useState("");
  const [input1ArrayValue, setInput1ArrayValue] = useState([]);
  const [input2ArrayValue, setInput2ArrayValue] = useState([]);
  const [input4ArrayValue, setInput4ArrayValue] = useState([]);
  const [input6ArrayValue, setInput6ArrayValue] = useState([]);
  const [input7ArrayValue, setInput7ArrayValue] = useState([]);
  const [input8ArrayValue, setInput8ArrayValue] = useState([]);
  const [input9ArrayValue, setInput9ArrayValue] = useState([]);
  const [input10ArrayValue, setInput10ArrayValue] = useState([]);
  const [input11ArrayValue, setInput11ArrayValue] = useState([]);
  const [input12ArrayValue, setInput12ArrayValue] = useState([]);
  const [input13ArrayValue, setInput13ArrayValue] = useState([]);

  // Get values of completed note from modal call back and call change status api function
  const handleModelCallback = (edit, selectedColumns, duplicate) => {
    if (edit === "add") {
      onColumnsChange(selectedColumns, duplicate);
      setOpenCustomColumnsModel(false);
    } else {
      setOpenCustomColumnsModel(false);
    }
  };

  const clearAll = (e) => {
    e.preventDefault();
    resetFilterFields();
    onSearchChange("clear");
  };

  const resetFilterFields = useCallback(() => {
    setInput1Value("");
    setInput2Value("");
    setInput3Value("");
    setInput4Value("");
    setInput5Value("");
    setInput6Value("");
    setInput7Value("");
    setInput8Value("");
    setInput9Value("");
    setInput10Value("");
    setInput11Value("");
    setInput12Value("");
    setInput13Value("");
    setInput14Value("");
    setInput1ArrayValue([]);
    setInput2ArrayValue([]);
    setInput4ArrayValue([]);
    setInput6ArrayValue([]);
    setInput7ArrayValue([]);
    setInput8ArrayValue([]);
    setInput9ArrayValue([]);
    setInput10ArrayValue([]);
    setInput11ArrayValue([]);
    setInput12ArrayValue([]);
    setInput13ArrayValue([]);
  }, []);
  const searchBtn = (e) => {
    e.preventDefault();
    onSearchChange(
      input1_type === "input" || input1_multiple === "no"
        ? input1Value
        : input1_type === "dropdown" && input1_multiple === "yes"
          ? input1ArrayValue
          : "",
      input2_type === "input" ||
        input2_type === "date" ||
        input2_multiple === "no"
        ? input2Value
        : input2_type === "dropdown" && input2_multiple === "yes"
          ? input2ArrayValue
          : "",
      input3_type === "input" ||
        input3_type === "date" ||
        input3_multiple === "no"
        ? input3Value
        : input3_type === "dropdown" && input3_multiple === "yes"
          ? ""
          : "",
      input4_type === "input" ||
        input4_type === "date" ||
        input4_multiple === "no"
        ? input4Value
        : input4_type === "dropdown" && input4_multiple === "yes"
          ? input4ArrayValue
          : "",
      input5Value,
      input6_type === "input" || input6_multiple === "no"
        ? input6Value
        : input6_type === "dropdown" && input6_multiple === "yes"
          ? input6ArrayValue
          : "",
      input7_type === "input" || input7_multiple === "no"
        ? input7Value
        : input7_type === "dropdown" && input7_multiple === "yes"
          ? input7ArrayValue
          : "",
      input8_type === "input" || input8_multiple === "no"
        ? input8Value
        : input8_type === "dropdown" && input8_multiple === "yes"
          ? input8ArrayValue
          : "",
      input9_type === "input" || input9_multiple === "no"
        ? input9Value
        : input9_type === "dropdown" && input9_multiple === "yes"
          ? input9ArrayValue
          : "",
      input10_type === "input" || input10_multiple === "no"
        ? input10Value
        : input10_type === "dropdown" && input10_multiple === "yes"
          ? input10ArrayValue
          : "",
      input11_type === "input" || input11_multiple === "no"
        ? input11Value
        : input10_type === "dropdown" && input11_multiple === "yes"
          ? input11ArrayValue
          : "",
      input12_type === "input" || input12_multiple === "no"
        ? input12Value
        : input12_type === "dropdown" && input12_multiple === "yes"
          ? input12ArrayValue
          : "",
      input13_type === "input" || input13_multiple === "no"
        ? input13Value
        : input13_type === "dropdown" && input13_multiple === "yes"
          ? input13ArrayValue
          : "",
      input14Value,
      showSignupDateFilter ? signupStartDate || null : undefined,
      showSignupDateFilter ? signupEndDate || null : undefined,
      showCreatedAtDateFilter ? createdAtStartDate || null : undefined,
      showCreatedAtDateFilter ? createdAtEndDate || null : undefined,
    );
  };
  const handle1InputChanges = (index) => {
    // Create a copy of the array to avoid mutating the original array directly
    const newArray = [...input1ArrayValue];
    // Remove the item at the specified index
    newArray.splice(index, 1);
    // Update the state or perform any desired operations with the modified array
    setInput1ArrayValue(newArray);
  };
  const handle2InputChanges = (index) => {
    // Create a copy of the array to avoid mutating the original array directly
    const newArray = [...input2ArrayValue];
    // Remove the item at the specified index
    newArray.splice(index, 1);
    // Update the state or perform any desired operations with the modified array
    setInput2ArrayValue(newArray);
  };
  const handle4InputChanges = (index) => {
    // Create a copy of the array to avoid mutating the original array directly
    const newArray = [...input4ArrayValue];
    // Remove the item at the specified index
    newArray.splice(index, 1);
    // Update the state or perform any desired operations with the modified array
    setInput4ArrayValue(newArray);
  };
  const handle6InputChanges = (index) => {
    // Create a copy of the array to avoid mutating the original array directly
    const newArray = [...input6ArrayValue];
    // Remove the item at the specified index
    newArray.splice(index, 1);
    // Update the state or perform any desired operations with the modified array
    setInput6ArrayValue(newArray);
  };
  const handle7InputChanges = (index) => {
    const newArray = [...input7ArrayValue];
    newArray.splice(index, 1);
    setInput7ArrayValue(newArray);
  };
  const handle8InputChanges = (index) => {
    const newArray = [...input8ArrayValue];
    newArray.splice(index, 1);
    setInput8ArrayValue(newArray);
  };
  const handle9InputChanges = (index) => {
    const newArray = [...input9ArrayValue];
    newArray.splice(index, 1);
    setInput9ArrayValue(newArray);
  };
  const handle10InputChanges = (index) => {
    const newArray = [...input10ArrayValue];
    newArray.splice(index, 1);
    setInput10ArrayValue(newArray);
  };
  const handle11InputChanges = (index) => {
    const newArray = [...input11ArrayValue];
    newArray.splice(index, 1);
    setInput11ArrayValue(newArray);
  };
  const handle12InputChanges = (index) => {
    const newArray = [...input12ArrayValue];
    newArray.splice(index, 1);
    setInput12ArrayValue(newArray);
  };
  const handle13InputChanges = (index) => {
    const newArray = [...input13ArrayValue];
    newArray.splice(index, 1);
    setInput13ArrayValue(newArray);
  };
  const handle2Focus = () => {
    setInput2Type("date");
  };
  const handle2Blur = () => {
    setInput2Type("text");
  };
  const handle3Focus = () => {
    setInput3Type("date");
  };
  const handle3Blur = () => {
    setInput3Type("text");
  };
  const handle4Focus = () => {
    setInput4Type("date");
  };
  const handle4Blur = () => {
    setInput4Type("text");
  };
  const handle5Focus = () => {
    setInput5Type("date");
  };
  const handle5Blur = () => {
    setInput5Type("text");
  };
  const sendValueDirectly = (value) => {
    setInput6Value(value);
    setInput1Value("");
    setInput2Value("");
    setInput3Value("");
    setInput4Value("");
    setInput5Value("");
    onSearchChange("", "", "", "", "", value);
    setInput7Value("");
    setInput7ArrayValue([]);
  };
  const handleSelectChange = (selectedOptions) => {
    setInput1ArrayValue(selectedOptions || []);
  };
  const handleSelectChange2 = (selectedOptions) => {
    setInput2ArrayValue(selectedOptions || []);
  };
  const handleSelectChange6 = (selectedOptions) => {
    setInput6ArrayValue(selectedOptions || []);
  };
  const handleSelectChange7 = (selectedOptions) => {
    setInput7ArrayValue(selectedOptions || []);
  };
  const handleSelectChange8 = (selectedOptions) => {
    setInput8ArrayValue(selectedOptions || []);
  };
  const handleSelectChange9 = (selectedOptions) => {
    setInput9ArrayValue(selectedOptions || []);
  };
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      height: "38px",
      alignItems: "center",
      boxShadow: "none",
      border: "none",
      background: "transparent",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "38px",
      padding: "0 8px",
      alignItems: "center",
    }),
    placeholder: (provided) => ({
      ...provided,
      display: "block",
      margin: 0,
      position: "static",
      transform: "none",
      top: "auto",
      lineHeight: "1.2",
      color: "#64748b",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "38px",
      alignItems: "center",
      marginLeft: "auto",
      flexShrink: 0,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
    }),
    multiValue: (provided) => ({
      ...provided,
      display: "none",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      display: "none",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      display: "none",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10050,
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 10050,
      marginTop: 4,
      overflow: "hidden",
      borderRadius: 10,
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
    }),
    menuList: (provided) => ({
      ...provided,
      paddingTop: 6,
      paddingBottom: 6,
      maxHeight: 240,
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: 12,
      padding: "9px 12px",
      margin: "0 6px",
      borderRadius: 6,
      whiteSpace: "normal",
      wordBreak: "break-word",
      overflow: "visible",
      textOverflow: "clip",
      lineHeight: 1.4,
      cursor: "pointer",
      backgroundColor: state.isFocused
        ? "rgba(0, 123, 255, 0.08)"
        : state.isSelected
          ? "rgba(0, 123, 255, 0.12)"
          : "#fff",
      color: state.isFocused || state.isSelected ? "#007bff" : "#35314d",
    }),
  };

  const premiumSelectClassNames = {
    menuPortal: () => "cms-clients-select-menu-portal",
    menu: () => "cms-clients-select-menu",
    menuList: () => "cms-clients-select-menu-list",
    option: ({ isFocused, isSelected }) =>
      `cms-clients-select-option${isFocused ? " is-focused" : ""}${isSelected ? " is-selected" : ""}`,
  };

  const suggestionSelectBehavior = {
    hideSelectedOptions: false,
    closeMenuOnSelect: false,
  };

  const SuggestionValueContainer = ({ children, ...props }) => {
    const selected = props.getValue();
    if (selected?.length) {
      const text =
        selected.length === 1
          ? selected[0].label || selected[0].name || selected[0].title
          : `${selected.length} selected`;
      return (
        <components.ValueContainer {...props}>
          <span
            className="min-w-0 flex-1 truncate text-xs font-medium text-blue-600"
            title={selected
              .map((option) => option.label || option.name || option.title)
              .join(", ")}
          >
            {text}
          </span>
          {children}
        </components.ValueContainer>
      );
    }
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  };

  const suggestionSelectComponents = premium
    ? {
        MultiValue: () => null,
        ValueContainer: ({ children, ...props }) => (
          <components.ValueContainer {...props}>{children}</components.ValueContainer>
        ),
      }
    : {
        MultiValue: () => null,
        ValueContainer: SuggestionValueContainer,
      };

  const suggestionSelectProps = {
    ...suggestionSelectBehavior,
    components: suggestionSelectComponents,
    controlShouldRenderValue: !premium,
    getOptionLabel: (option) =>
      option.label ||
      option.name ||
      option.title ||
      option.agencyname ||
      "",
    getOptionValue: (option) =>
      option.value ??
      option.id ??
      option.label ??
      option.name ??
      option.title ??
      option.agencyname,
  };

  const premiumSelectMenuProps =
    premium && typeof document !== "undefined"
      ? {
          classNames: premiumSelectClassNames,
          menuPlacement: "auto",
        }
      : {};

  const renderPremiumMultiSelect = (options, selected, setSelected, searchHint) => (
    <CmsFilterMultiSelect
      embedded
      options={options || []}
      value={selected}
      onChange={setSelected}
      placeholder="All"
      searchPlaceholder={searchHint}
    />
  );

  const renderPremiumSingleSelect = (options, selectedValue, setValue, searchHint) => (
    <CmsFilterSingleSelect
      embedded
      options={options || []}
      value={selectedValue}
      onChange={setValue}
      placeholder="All"
      searchPlaceholder={searchHint}
    />
  );

  const runSearch = useCallback(
    (e) => {
      if (e?.preventDefault) e.preventDefault();
      searchBtn(e);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      input1Value,
      input2Value,
      input3Value,
      input4Value,
      input5Value,
      input6Value,
      input7Value,
      input8Value,
      input9Value,
      input1ArrayValue,
      input2ArrayValue,
      input4ArrayValue,
      input6ArrayValue,
      input7ArrayValue,
      input8ArrayValue,
      input9ArrayValue,
    ],
  );

  const handleFilterEnterKey = useCallback(
    (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      runSearch(e);
    },
    [runSearch],
  );

  useEffect(() => {
    if (searchRef) {
      searchRef(runSearch);
    }
  }, [searchRef, runSearch]);

  const openColumnsSettings = useCallback(() => {
    setOpenCustomColumnsModel(true);
  }, []);

  useEffect(() => {
    if (columnsSettingsRef) {
      columnsSettingsRef(openColumnsSettings);
    }
  }, [columnsSettingsRef, openColumnsSettings]);

  useEffect(() => {
    if (clearFiltersRef) {
      clearFiltersRef(resetFilterFields);
    }
  }, [clearFiltersRef, resetFilterFields]);

  const arraysShallowEqual = (a, b) => {
    if (a === b) return true;
    const left = a || [];
    const right = b || [];
    return JSON.stringify(left) === JSON.stringify(right);
  };

  const filterArraySyncKey = filterArraySync ? JSON.stringify(filterArraySync) : "";
  useEffect(() => {
    if (!filterArraySync) return;
    const sync = filterArraySync;
    if (sync.input6 !== undefined && !arraysShallowEqual(sync.input6, input6ArrayValue)) {
      setInput6ArrayValue(Array.isArray(sync.input6) ? sync.input6 : []);
    }
    if (sync.input7 !== undefined && !arraysShallowEqual(sync.input7, input7ArrayValue)) {
      setInput7ArrayValue(Array.isArray(sync.input7) ? sync.input7 : []);
    }
    if (sync.input8 !== undefined && !arraysShallowEqual(sync.input8, input8ArrayValue)) {
      setInput8ArrayValue(Array.isArray(sync.input8) ? sync.input8 : []);
    }
    if (sync.input9 !== undefined && !arraysShallowEqual(sync.input9, input9ArrayValue)) {
      setInput9ArrayValue(Array.isArray(sync.input9) ? sync.input9 : []);
    }
    if (sync.input10 !== undefined && !arraysShallowEqual(sync.input10, input10ArrayValue)) {
      setInput10ArrayValue(Array.isArray(sync.input10) ? sync.input10 : []);
    }
    if (sync.input11 !== undefined && !arraysShallowEqual(sync.input11, input11ArrayValue)) {
      setInput11ArrayValue(Array.isArray(sync.input11) ? sync.input11 : []);
    }
    if (sync.input12 !== undefined && !arraysShallowEqual(sync.input12, input12ArrayValue)) {
      setInput12ArrayValue(Array.isArray(sync.input12) ? sync.input12 : []);
    }
    if (sync.input13 !== undefined && !arraysShallowEqual(sync.input13, input13ArrayValue)) {
      setInput13ArrayValue(Array.isArray(sync.input13) ? sync.input13 : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterArraySyncKey]);

  const filterScalarSyncKey = filterScalarSync ? JSON.stringify(filterScalarSync) : "";
  useEffect(() => {
    if (!filterScalarSync) return;
    const s = filterScalarSync;
    if (s.input1 !== undefined && s.input1 !== input1Value) setInput1Value(s.input1 || "");
    if (s.input2 !== undefined && s.input2 !== input2Value) setInput2Value(s.input2 || "");
    if (s.input3 !== undefined && s.input3 !== input3Value) setInput3Value(s.input3 || "");
    if (s.input4 !== undefined && s.input4 !== input4Value) setInput4Value(s.input4 || "");
    if (s.input5 !== undefined && s.input5 !== input5Value) setInput5Value(s.input5 || "");
    if (s.input10 !== undefined && s.input10 !== input10Value) setInput10Value(s.input10 || "");
    if (s.input11 !== undefined && s.input11 !== input11Value) setInput11Value(s.input11 || "");
    if (s.input14 !== undefined && s.input14 !== input14Value) setInput14Value(s.input14 || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterScalarSyncKey]);

  const premiumAutoApplyBootstrapped = useRef(false);
  useEffect(() => {
    if (!premium || !hideSearchButton) return undefined;

    if (!premiumAutoApplyBootstrapped.current) {
      premiumAutoApplyBootstrapped.current = true;
      return undefined;
    }

    const timer = window.setTimeout(() => {
      runSearch({ preventDefault: () => {} });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [
    premium,
    hideSearchButton,
    runSearch,
    input5Value,
    input6ArrayValue,
    input7ArrayValue,
    input8ArrayValue,
    input9ArrayValue,
    input10ArrayValue,
    input10Value,
    input11ArrayValue,
    input11Value,
    input12ArrayValue,
    input13ArrayValue,
    signupStartDate,
    signupEndDate,
    createdAtStartDate,
    createdAtEndDate,
  ]);

  useEffect(() => {
    if (!premium) return undefined;

    const closeOpenFilterDropdowns = (event) => {
      document
        .querySelectorAll(
          ".tailwind-container .optionListContainer.displayBlock",
        )
        .forEach((panel) => {
          const container = panel.closest(".multiSelectContainer");
          if (container && !container.contains(event.target)) {
            const input = container.querySelector(".searchBox, input");
            input?.blur();
          }
        });

      document
        .querySelectorAll(
          ".tailwind-container .select__control--menu-is-open",
        )
        .forEach((control) => {
          const container = control.closest(
            ".multiSelectContainer",
          );
          if (container && !container.contains(event.target)) {
            control.querySelector("input")?.blur();
          }
        });
    };

    document.addEventListener("mousedown", closeOpenFilterDropdowns);
    return () => {
      document.removeEventListener("mousedown", closeOpenFilterDropdowns);
    };
  }, [premium]);

  return (
    <>
      <PremiumDenseContext.Provider value={Boolean(premium && premiumDense)}>
      <div
        className={`${
          premium
            ? premiumDense
              ? "mb-0 min-w-0 flex-1"
              : "mb-0 w-full"
            : "row mb-3"
        }`}
      >
        <form
          className="w-full"
          onSubmit={runSearch}
          onKeyDown={handleFilterEnterKey}
        >
          <div
            className={
              premium
                ? premiumDense
                  ? "grid w-full grid-cols-1 items-end gap-2 sm:grid-cols-2"
                  : premiumFixedColumns === 2
                    ? "cms-filters-advanced-grid cms-filters-advanced-grid--two-only grid w-full grid-cols-1 items-end gap-3 sm:grid-cols-2"
                    : "cms-filters-advanced-grid grid w-full grid-cols-1 items-end gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
                : "row inner-form py-2 px-3"
            }
          >
            <div
              className={
                premium && hideTopToolbar
                  ? "hidden"
                  : "flex justify-end"
              }
            >
              {allColumns && (
                <div className="pt-0 pointer pr-1">
                  <FcIcons.FcAutomatic
                    className="mb-1"
                    onClick={() => setOpenCustomColumnsModel(true)}
                    size={20}
                  />
                </div>
              )}
              {allColumns && !hideClearAll && (
                <div className="pt-0 pointer pr-1">
                  <div
                    className="size-13 pt-1 font-sans"
                    style={{ color: "#4a4aff" }}
                    onClick={(e) => clearAll(e)}
                  >
                    {" "}
                    Clear All{" "}
                  </div>
                </div>
              )}
            </div>
            {/* seacrh input section */}

            {/* Input 1 fields */}
            {input1_type === "input" && input1 && (
              <div className={premiumColClass(premium)}>
                <PremiumFieldWrap
                  premium={premium}
                  label={getPremiumLabel(premiumLabels, "input1")}
                  icon={getPremiumIcon(premiumIcons, "input1")}
                >
                  {premium ? (
                    <input
                      type="text"
                      className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-xs text-slate-800 outline-none"
                      value={input1Value}
                      placeholder={
                        typeof input1 === "string"
                          ? input1
                          : "Name or client/user ID"
                      }
                      onChange={(e) => setInput1Value(e.target.value)}
                      onKeyDown={handleFilterEnterKey}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                      value={input1Value}
                      placeholder={input1}
                      onChange={(e) => setInput1Value(e.target.value)}
                      onKeyDown={handleFilterEnterKey}
                    />
                  )}
                </PremiumFieldWrap>
              </div>
            )}
            {/* Input 1 fields */}
            {input1_type === "dropdown" &&
              input1_multiple === "no" &&
              input1 && (
                <div className="col-lg-2 col-6">
                  <select
                    value={input1}
                    onChange={(e) => setInput1Value(e.target.value)}
                    className="form-select s-dropdown pb-2 shadow-sm pt-1 w-lg-90 mt-1 form-control"
                  >
                    <option disabled>{input1}</option>
                    {[]
                      .concat(input1Data)
                      .sort((a, b) => (a.name > b.name ? 1 : -1))
                      .map((value) => (
                        <option
                          key={value.id}
                          className={input1 === value.name ? "selected" : ""}
                        >
                          {value.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            {input1_type === "dropdown" &&
              input1_multiple === "yes" &&
              input1 && (
                <div className="col-lg-2 col-6">
                  {input1_suggestion ? (
                    <Select
                      isMulti
                      name="participants"
                      options={input1Data.filter(
                        (option) => !input1ArrayValue.includes(option),
                      )} // Filter out selected values
                      className="s-dropdown search py-0 shadow-sm mt-1 chip-hide w-lg-90"
                      classNamePrefix="select"
                      onChange={handleSelectChange}
                      placeholder={input1} // Use dynamic placeholder
                      noOptionsMessage={() => "No options available"}
                      value={input1ArrayValue} // Manage selected values
                      styles={customStyles}
                      {...suggestionSelectProps}
                    />
                  ) : (
                    <PremiumMultiselect
                      premium={premium}
                      options={input1Data
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))}
                      className="form-select s-dropdown py-1 shadow-sm mt-1 chip-hide w-lg-90"
                      placeholder={input1}
                      displayValue={"name"}
                      onSelect={(selected) => setInput1ArrayValue(selected)}
                      showCheckbox={true}
                      disablePreSelectedValues={true}
                      hidePlaceholder={false}
                      selectedValues={input1ArrayValue}
                    />
                  )}
                </div>
              )}

            {/* Input 2 fields */}
            {input2_type === "input" && input2 && (
              <div className={premiumColClass(premium)}>
                <PremiumFieldWrap
                  premium={premium}
                  label={getPremiumLabel(premiumLabels, "input2")}
                  icon={getPremiumIcon(premiumIcons, "input2")}
                >
                  {premium ? (
                    <input
                      type="text"
                      className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-xs text-slate-800 outline-none"
                      value={input2Value}
                      placeholder={
                        typeof input2 === "string" ? input2 : "Search email..."
                      }
                      onChange={(e) => setInput2Value(e.target.value)}
                      onKeyDown={handleFilterEnterKey}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                      value={input2Value}
                      placeholder={input2}
                      onChange={(e) => setInput2Value(e.target.value)}
                      onKeyDown={handleFilterEnterKey}
                    />
                  )}
                </PremiumFieldWrap>
              </div>
            )}
            {input2_type === "date" && input2 && (
              <div className="col-lg-2 col-6">
                <input
                  type={input2Type}
                  className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                  onChange={(e) => setInput2Value(e.target.value)}
                  placeholder={input2}
                  onFocus={handle2Focus}
                  onBlur={handle2Blur}
                  value={input2Value}
                />
              </div>
            )}
            {/* Input 2 fields */}
            {input2_type === "dropdown" &&
              input2_multiple === "no" &&
              input2 &&
              input2Data && (
                <div className="col-lg-2 col-6">
                  <select
                    value={input2}
                    onChange={(e) => setInput2Value(e.target.value)}
                    className="form-select s-dropdown pb-2 shadow-sm pt-1 w-lg-90 mt-1 form-control"
                  >
                    <option disabled>{input2}</option>
                    {input2Data.map((value) => (
                      <option
                        key={value.id}
                        className={input2 === value.name ? "selected" : ""}
                      >
                        {value.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {input2_type === "dropdown" &&
              input2_multiple === "yes" &&
              input2Data && (
                <div className="col-lg-2 col-6">
                  {input2_suggestion ? (
                    <Select
                      isMulti
                      name="participants"
                      options={input2Data.filter(
                        (option) => !input2ArrayValue.includes(option),
                      )} // Filter out selected values
                      className="s-dropdown search py-0 shadow-sm mt-1 chip-hide w-lg-90"
                      classNamePrefix="select"
                      onChange={handleSelectChange2}
                      placeholder={input2} // Use dynamic placeholder
                      noOptionsMessage={() => "No options available"}
                      value={input2ArrayValue} // Manage selected values
                      styles={customStyles}
                      {...suggestionSelectProps}
                    />
                  ) : (
                    <PremiumMultiselect
                      premium={premium}
                      options={input2Data
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))}
                      className="form-select s-dropdown py-1 shadow-sm mt-1 chip-hide w-lg-90"
                      placeholder={input2}
                      displayValue={"name"}
                      onSelect={(selected) => setInput2ArrayValue(selected)}
                      showCheckbox={true}
                      disablePreSelectedValues={true}
                      hidePlaceholder={false}
                      selectedValues={input2ArrayValue}
                    />
                  )}
                </div>
              )}

            {/* Input 3 fields */}
            {input3_type === "input" && input3 && (
              <div className={premiumColClass(premium)}>
                <PremiumFieldWrap
                  premium={premium}
                  label={getPremiumLabel(premiumLabels, "input3")}
                  icon={getPremiumIcon(premiumIcons, "input3")}
                >
                  {premium ? (
                    <input
                      type="text"
                      className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-xs text-slate-800 outline-none"
                      value={input3Value}
                      placeholder={
                        typeof input3 === "string" ? input3 : "Search phone..."
                      }
                      onChange={(e) => setInput3Value(e.target.value)}
                      onKeyDown={handleFilterEnterKey}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                      value={input3Value}
                      placeholder={input3}
                      onChange={(e) => setInput3Value(e.target.value)}
                      onKeyDown={handleFilterEnterKey}
                    />
                  )}
                </PremiumFieldWrap>
              </div>
            )}
            {input3_type === "date" && input3 && (
              <div className="col-lg-2 col-6">
                <input
                  type={input3Type}
                  className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                  onChange={(e) => setInput3Value(e.target.value)}
                  placeholder={input3}
                  onFocus={handle3Focus}
                  onBlur={handle3Blur}
                  value={input3Value}
                />
              </div>
            )}
            {/* Input 3 fields */}
            {/* {input3_type === 'dropdown' && input3_multiple === 'no' &&
        input3 && input3Data &&
        <div className='col-lg-2 col-6'>
        <select value={input1} onChange={(e) => setInput3Value(e.target.value)}
        className="form-select s-dropdown pb-2 shadow-sm pt-1 w-lg-90 mt-1 form-control">
        <option>{input3}</option>
        {input3Data.map((value) => (
            <option key={input3_filter_by && input3_filter_by === 'title' ? value.value : value.value} 
            className={input3 === value.label ? "selected" : ""}>
            {input3_filter_by && input3_filter_by === 'title' ? value.title : value.label}
            </option>
        ))}
        </select>
        </div>} */}
            {input3_type === "dropdown" &&
              input3_multiple === "no" &&
              input3 &&
              input3Data && (
                <div className={premiumColClass(premium)}>
                  <PremiumFieldWrap
                    premium={premium}
                    label={premium ? getPremiumLabel(premiumLabels, "input3") : undefined}
                    icon={premium ? getPremiumIcon(premiumIcons, "input3") : undefined}
                    hasSelection={Boolean(input3Value)}
                  >
                    <select
                      value={input3Value}
                      onChange={(e) => setInput3Value(e.target.value)}
                      className={
                        premium
                          ? PREMIUM_NATIVE_SELECT_CLASS
                          : "form-select s-dropdown pb-2 shadow-sm pt-1 w-lg-90 mt-1 form-control"
                      }
                    >
                      {!premium ? null : (
                        <option value="">All types</option>
                      )}
                      {input3Data.map((item) => (
                        <option
                          key={item.value || item.label}
                          value={item.value}
                        >
                          {input3_filter_by && input3_filter_by === "title"
                            ? item.title
                            : item.label}
                        </option>
                      ))}
                    </select>
                  </PremiumFieldWrap>
                </div>
              )}
            {/* Input 4 fields */}
            {input4_type === "input" && input4 && !premium && (
              <div className="col-lg-2 col-6">
                <input
                  type="text"
                  className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                  value={input4Value}
                  placeholder={input4}
                  onChange={(e) => setInput4Value(e.target.value)}
                  onKeyDown={handleFilterEnterKey}
                />
              </div>
            )}
            {input4_type === "date" && input4 && !premium && (
              <div className="col-lg-2 col-6">
                <input
                  type={input4Type}
                  className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                  onChange={(e) => setInput4Value(e.target.value)}
                  placeholder={input4}
                  onFocus={handle4Focus}
                  onBlur={handle4Blur}
                  value={input4Value}
                />
              </div>
            )}
            {premium && input4_type === "date" && input4 && (
              <div className={premiumColClass(premium)}>
                <PremiumDateField
                  label={getPremiumLabel(premiumLabels, "input4")}
                  name="input4Date"
                  value={input4Value}
                  onChange={(e) => setInput4Value(e.target.value)}
                />
              </div>
            )}

            {input4_type === "dropdown" &&
              input4_multiple === "yes" &&
              input4 && (
                <div className={premiumColClass(premium)}>
                  <PremiumFieldWrap
                    premium={premium}
                    label={premium ? getPremiumLabel(premiumLabels, "input4") : undefined}
                    icon={premium ? getPremiumIcon(premiumIcons, "input4") : undefined}
                    hasSelection={Boolean(input4ArrayValue?.length)}
                  >
                    <PremiumMultiselect
                      premium={premium}
                      options={input4Data
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))}
                      className={premiumMsClass(premium)}
                      placeholder={premium ? "All assignees" : input4}
                      displayValue="name"
                      onSelect={(selected) => setInput4ArrayValue(selected)}
                      onRemove={(selected) => setInput4ArrayValue(selected)}
                      showCheckbox={true}
                      disablePreSelectedValues={true}
                      hidePlaceholder={false}
                      selectedValues={input4ArrayValue}
                    />
                  </PremiumFieldWrap>
                </div>
              )}
            {input4_type === "dropdown" &&
              input4_multiple === "no" &&
              input4 && (
                <div className="col-lg-2 col-6">
                  <select
                    value={input4}
                    onChange={(e) => setInput4Value(e.target.value)}
                    className="form-select s-dropdown pb-2 shadow-sm pt-1 w-lg-90 mt-1 form-control"
                  >
                    <option selected>{input4}</option>
                    {input4Data.map((value) => (
                      <option
                        key={
                          input4_filter_by && input4_filter_by === "label"
                            ? value.value
                            : input4_filter_by === "agents"
                              ? value.agencyname
                              : value.title
                        }
                        value={
                          input4_filter_by && input4_filter_by === "label"
                            ? value.value
                            : input4_filter_by === "agents"
                              ? value.agencyname
                              : value.id
                        }
                        className={input4 === value.title ? "selected" : ""}
                      >
                        {input4_filter_by && input4_filter_by === "label"
                          ? value.label
                          : input4_filter_by === "agents"
                            ? value.agencyname
                            : value.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            {/* Input 5 fields */}
            {input5_type === "input" && input5 && (
              <div className="col-lg-2 col-6">
                <input
                  type="text"
                  className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                  value={input5Value}
                  placeholder={input5}
                  onChange={(e) => setInput5Value(e.target.value)}
                  onKeyDown={handleFilterEnterKey}
                />
              </div>
            )}
            {input5_type === "date" && input5 && !premium && (
              <div className="col-lg-2 col-6">
                <input
                  type={input5Type}
                  className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                  onChange={(e) => setInput5Value(e.target.value)}
                  placeholder={input5}
                  onFocus={handle5Focus}
                  onBlur={handle5Blur}
                  value={input5Value}
                />
              </div>
            )}
            {premium && input5_type === "date" && input5 && (
              <div className={premiumColClass(premium)}>
                <PremiumDateField
                  label={getPremiumLabel(premiumLabels, "input5")}
                  name="input5Date"
                  value={input5Value}
                  onChange={(e) => setInput5Value(e.target.value)}
                />
              </div>
            )}
            {input5_type === "dropdown" &&
              input5_multiple === "no" &&
              input5 &&
              !premium && (
                <div className="col-lg-2 col-6">
                  <select
                    value={input5Value}
                    onChange={(e) => setInput5Value(e.target.value)}
                    className="form-select s-dropdown pb-2 shadow-sm pt-1 w-lg-90 mt-1 form-control"
                  >
                    <option value="">{input5}</option>
                    {input5Data.map((value) => (
                      <option key={value.title} value={value.title}>
                        {value.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            {/* Input 6 fields */}
            {input6_type === "input" && input6 && (
              <div className="col-lg-2 col-6">
                <input
                  type="text"
                  className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                  value={input6Value}
                  placeholder={input6}
                  onChange={(e) => setInput6Value(e.target.value)}
                  onKeyDown={handleFilterEnterKey}
                />
              </div>
            )}
            {/* Input 6 fields dropdown */}
            {input6_type === "dropdown" &&
              input6_multiple === "yes" &&
              input6 && (
                <div className={premiumColClass(premium)}>
                  <PremiumFieldWrap
                    premium={premium}
                    label={getPremiumLabel(premiumLabels, "input6")}
                    icon={getPremiumIcon(premiumIcons, "input6")}
                    hasSelection={Boolean(input6ArrayValue?.length)}
                  >
                    {premium
                      ? renderPremiumMultiSelect(
                          input6_filter_by && input6_filter_by === "agents"
                            ? sortByAgencyName(input6Data)
                            : sortByTitle(input6Data),
                          input6ArrayValue,
                          setInput6ArrayValue,
                          `Search ${getPremiumLabel(premiumLabels, "input6").toLowerCase()}...`,
                        )
                      : input6_suggestion ? (
                        <Select
                          isMulti
                          name="input6Multi"
                          options={input6Data}
                          className={premiumSelectClass(premium)}
                          classNamePrefix="select"
                          onChange={handleSelectChange6}
                          placeholder={input6}
                          noOptionsMessage={() => "No options available"}
                          value={input6ArrayValue}
                          styles={customStyles}
                          {...suggestionSelectProps}
                          {...premiumSelectMenuProps}
                          menuPosition="absolute"
                        />
                      ) : (
                        <PremiumMultiselect
                          premium={premium}
                          options={
                            input6_filter_by && input6_filter_by === "agents"
                              ? sortByAgencyName(input6Data)
                              : sortByTitle(input6Data)
                          }
                          className={premiumMsClass(premium)}
                          placeholder={input6}
                          displayValue={
                            input6_filter_by && input6_filter_by === "agents"
                              ? "agencyname"
                              : "title"
                          }
                          onSelect={(selected) => setInput6ArrayValue(selected)}
                          showCheckbox={true}
                          disablePreSelectedValues={true}
                          hidePlaceholder={false}
                          selectedValues={input6ArrayValue}
                        />
                      )}
                  </PremiumFieldWrap>
                </div>
              )}
            {input6_type === "dropdown" &&
              input6_multiple === "no" &&
              input6 && (
                <div className="col-lg-2 col-6">
                  <select
                    value={input6}
                    onChange={(e) =>
                      input6CallBack
                        ? sendValueDirectly(e.target.value)
                        : setInput6Value(e.target.value)
                    }
                    className="form-select s-dropdown pb-2 shadow-sm pt-1 w-lg-90 mt-1 form-control"
                  >
                    <option disabled>{input6}</option>
                    {input6Data.map((value) => (
                      <option
                        key={value.title}
                        className={input6 === value.title ? "selected" : ""}
                      >
                        {value.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {/* Input 7 fields */}
            {input7_type === "dropdown" &&
              input7_multiple === "no" &&
              input7 && (
                <div className="col-lg-2 col-6">
                  <select
                    value={input7}
                    onChange={(e) => setInput7Value(e.target.value)}
                    className="form-select s-dropdown pb-2 shadow-sm pt-1 w-lg-90 mt-1 form-control"
                  >
                    <option disabled>{input7}</option>
                    {input7Data.map((value) => (
                      <option
                        key={value.title}
                        className={input7 === value.title ? "selected" : ""}
                      >
                        {value.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            {/* Input 7 fields */}
            {input7_type === "dropdown" &&
              input7_multiple === "yes" &&
              input7 && (
                <div className={premiumColClass(premium)}>
                  <PremiumFieldWrap
                    premium={premium}
                    label={getPremiumLabel(premiumLabels, "input7")}
                    icon={getPremiumIcon(premiumIcons, "input7")}
                    hasSelection={Boolean(input7ArrayValue?.length)}
                  >
                    {premium
                      ? renderPremiumMultiSelect(
                          input7_filter_by && input7_filter_by === "agents"
                            ? sortByAgencyName(input7Data)
                            : input7_filter_by === "title"
                              ? sortByTitle(input7Data)
                              : sortByName(input7Data),
                          input7ArrayValue,
                          setInput7ArrayValue,
                          `Search ${getPremiumLabel(premiumLabels, "input7").toLowerCase()}...`,
                        )
                      : input7_suggestion ? (
                        <Select
                          isMulti
                          name="participants"
                          options={input7Data}
                          className={premiumSelectClass(premium)}
                          classNamePrefix="select"
                          onChange={handleSelectChange7}
                          placeholder={input7}
                          noOptionsMessage={() => "No options available"}
                          value={input7ArrayValue}
                          styles={customStyles}
                          {...suggestionSelectProps}
                          {...premiumSelectMenuProps}
                          menuPosition="absolute"
                        />
                      ) : (
                        <PremiumMultiselect
                          premium={premium}
                          options={
                            input7_filter_by && input7_filter_by === "agents"
                              ? sortByAgencyName(input7Data)
                              : input7_filter_by === "title"
                                ? sortByTitle(input7Data)
                                : sortByName(input7Data)
                          }
                          className={premiumMsClass(premium)}
                          placeholder={input7}
                          displayValue={
                            input7_filter_by && input7_filter_by === "agents"
                              ? "agencyname"
                              : input7_filter_by === "title"
                                ? "title"
                                : "name"
                          }
                          onSelect={(selected) => setInput7ArrayValue(selected)}
                          showCheckbox={true}
                          disablePreSelectedValues={true}
                          hidePlaceholder={false}
                          selectedValues={input7ArrayValue}
                        />
                      )}
                  </PremiumFieldWrap>
                </div>
              )}

            {showSignupDateFilter && signupDateFilterAfterInput7 && !premium && (
              <>
                <div className="col-lg-3 col-md-4 col-6">
                  <div className="d-flex align-items-center m-1 signup-date-filter-inline">
                    <label className="s-label signup-date-inline-label mb-0">
                      Contract Start Date
                    </label>
                    <input
                      type="date"
                      name="signupStartDate"
                      className="form-control search bg-white shadow-sm signup-date-inline-input"
                      value={signupStartDate || ""}
                      onChange={onSignupDateChange}
                    />
                  </div>
                </div>
                <div className="col-lg-3 col-md-4 col-6">
                  <div className="d-flex align-items-center m-1 signup-date-filter-inline">
                    <label className="s-label signup-date-inline-label mb-0">
                      Contract End Date
                    </label>
                    <input
                      type="date"
                      name="signupEndDate"
                      className="form-control search bg-white shadow-sm signup-date-inline-input"
                      value={signupEndDate || ""}
                      min={signupStartDate || undefined}
                      disabled={!signupStartDate}
                      onChange={onSignupDateChange}
                    />
                  </div>
                </div>
              </>
            )}

            {showCreatedAtDateFilter && !premium && (
              <>
                <div className="col-lg-3 col-md-4 col-6">
                  <div className="d-flex align-items-center m-1 signup-date-filter-inline">
                    <label className="s-label signup-date-inline-label mb-0">
                      {createdAtDateLabels.start}
                    </label>
                    <input
                      type="date"
                      name="createdAtStartDate"
                      className="form-control search bg-white shadow-sm signup-date-inline-input"
                      value={createdAtStartDate || ""}
                      onChange={onCreatedAtDateChange}
                    />
                  </div>
                </div>
                <div className="col-lg-3 col-md-4 col-6">
                  <div className="d-flex align-items-center m-1 signup-date-filter-inline">
                    <label className="s-label signup-date-inline-label mb-0">
                      {createdAtDateLabels.end}
                    </label>
                    <input
                      type="date"
                      name="createdAtEndDate"
                      className="form-control search bg-white shadow-sm signup-date-inline-input"
                      value={createdAtEndDate || ""}
                      min={createdAtStartDate || undefined}
                      disabled={!createdAtStartDate}
                      onChange={onCreatedAtDateChange}
                    />
                  </div>
                </div>
              </>
            )}

            {premium && showCreatedAtDateFilter && (
              <>
                <div className={premiumColClass(premium)}>
                  <PremiumDateField
                    label={createdAtDateLabels.start}
                    name="createdAtStartDate"
                    value={createdAtStartDate}
                    onChange={onCreatedAtDateChange}
                  />
                </div>
                <div className={premiumColClass(premium)}>
                  <PremiumDateField
                    label={createdAtDateLabels.end}
                    name="createdAtEndDate"
                    value={createdAtEndDate}
                    min={createdAtStartDate || undefined}
                    disabled={!createdAtStartDate}
                    onChange={onCreatedAtDateChange}
                  />
                </div>
              </>
            )}

            {/* Input 8 fields */}
            {input8_type === "dropdown" &&
              input8_multiple === "yes" &&
              input8 && (
                <div className={premiumColClass(premium)}>
                  <PremiumFieldWrap
                    premium={premium}
                    label={getPremiumLabel(premiumLabels, "input8")}
                    icon={getPremiumIcon(premiumIcons, "input8")}
                    hasSelection={Boolean(input8ArrayValue?.length)}
                  >
                    {premium
                      ? renderPremiumMultiSelect(
                          input8_filter_by && input8_filter_by === "name"
                            ? sortByName(input8Data)
                            : sortByTitle(input8Data),
                          input8ArrayValue,
                          setInput8ArrayValue,
                          `Search ${getPremiumLabel(premiumLabels, "input8").toLowerCase()}...`,
                        )
                      : input8_suggestion ? (
                        <Select
                          isMulti
                          name="participants"
                          options={input8Data}
                          className={premiumSelectClass(premium)}
                          classNamePrefix="select"
                          onChange={handleSelectChange8}
                          placeholder={input8}
                          noOptionsMessage={() => "No options available"}
                          value={input8ArrayValue}
                          styles={customStyles}
                          {...suggestionSelectProps}
                          {...premiumSelectMenuProps}
                          menuPosition="absolute"
                        />
                      ) : (
                        <PremiumMultiselect
                          premium={premium}
                          options={
                            input8_filter_by && input8_filter_by === "name"
                              ? sortByName(input8Data)
                              : sortByTitle(input8Data)
                          }
                          className={premiumMsClass(premium)}
                          placeholder={input8}
                          displayValue={
                            input8_filter_by && input8_filter_by === "name"
                              ? "name"
                              : "title"
                          }
                          onSelect={(selected) => setInput8ArrayValue(selected)}
                          showCheckbox={true}
                          disablePreSelectedValues={true}
                          hidePlaceholder={false}
                          selectedValues={input8ArrayValue}
                        />
                      )}
                  </PremiumFieldWrap>
                </div>
              )}
            {input8_type === "dropdown" &&
              input8_multiple === "no" &&
              input8 && (
                <div className="col-lg-2 col-6">
                  <select
                    value={input8}
                    onChange={(e) => setInput8Value(e.target.value)}
                    className="form-select s-dropdown pb-2 shadow-sm pt-1 w-lg-90 mt-1 form-control"
                  >
                    <option disabled>{input8}</option>
                    {input8Data.map((value) => (
                      <option
                        key={value.title}
                        className={input8 === value.title ? "selected" : ""}
                        value={value.title}
                      >
                        {value.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {/* Input 9 fields */}
            {input9_type === "dropdown" &&
              input9_multiple === "yes" &&
              input9 && (
                <div className={premiumColClass(premium)}>
                  <PremiumFieldWrap
                    premium={premium}
                    label={getPremiumLabel(premiumLabels, "input9")}
                    icon={getPremiumIcon(premiumIcons, "input9")}
                    hasSelection={Boolean(input9ArrayValue?.length)}
                  >
                    {premium
                      ? renderPremiumMultiSelect(
                          input9_filter_by && input9_filter_by === "name"
                            ? sortByName(input9Data)
                            : sortByLabel(input9Data),
                          input9ArrayValue,
                          setInput9ArrayValue,
                          `Search ${getPremiumLabel(premiumLabels, "input9").toLowerCase()}...`,
                        )
                      : input9_suggestion ? (
                        <Select
                          isMulti
                          name="participants"
                          options={input9Data}
                          className={premiumSelectClass(premium)}
                          classNamePrefix="select"
                          onChange={handleSelectChange9}
                          placeholder={input9}
                          noOptionsMessage={() => "No options available"}
                          value={input9ArrayValue}
                          styles={customStyles}
                          {...suggestionSelectProps}
                          {...premiumSelectMenuProps}
                          menuPosition="absolute"
                        />
                      ) : (
                        <PremiumMultiselect
                          premium={premium}
                          options={
                            input9_filter_by && input9_filter_by === "name"
                              ? sortByName(input9Data)
                              : sortByTitle(input9Data)
                          }
                          className={premiumMsClass(premium)}
                          placeholder={input9}
                          displayValue={
                            input9_filter_by && input9_filter_by === "name"
                              ? "name"
                              : "title"
                          }
                          onSelect={(selected) => setInput9ArrayValue(selected)}
                          showCheckbox={true}
                          disablePreSelectedValues={true}
                          hidePlaceholder={false}
                          selectedValues={input9ArrayValue}
                        />
                      )}
                  </PremiumFieldWrap>
                </div>
              )}

            {premium && showSignupDateFilter && (
              <>
                <div className={premiumColClass(premium)}>
                  <PremiumDateField
                    label={signupDateLabels.start}
                    name="signupStartDate"
                    value={signupStartDate}
                    onChange={onSignupDateChange}
                  />
                </div>
                <div className={premiumColClass(premium)}>
                  <PremiumDateField
                    label={signupDateLabels.end}
                    name="signupEndDate"
                    value={signupEndDate}
                    min={signupStartDate || undefined}
                    disabled={!signupStartDate}
                    onChange={onSignupDateChange}
                  />
                </div>
              </>
            )}

            {premium &&
              input10_type === "dropdown" &&
              input10_multiple === "yes" &&
              input10 && (
                <div className={premiumColClass(premium)}>
                  <PremiumFieldWrap
                    premium={premium}
                    label={getPremiumLabel(premiumLabels, "input10")}
                    icon={getPremiumIcon(premiumIcons, "input10")}
                    hasSelection={Boolean(input10ArrayValue?.length)}
                  >
                    {renderPremiumMultiSelect(
                      input10_filter_by && input10_filter_by === "name"
                        ? sortByName(input10Data)
                        : sortByTitle(input10Data),
                      input10ArrayValue,
                      setInput10ArrayValue,
                      `Search ${getPremiumLabel(premiumLabels, "input10").toLowerCase()}...`,
                    )}
                  </PremiumFieldWrap>
                </div>
              )}

            {premium && (
              <>
                {input4_type === "input" && input4 && (
                  <div className={premiumColClass(premium)}>
                    <PremiumFieldWrap
                      premium={premium}
                      label="Country"
                      icon={<FaGlobeAmericas size={12} />}
                    >
                      <input
                        type="text"
                        className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-xs text-slate-800 outline-none"
                        value={input4Value}
                        placeholder="Search country..."
                        onChange={(e) => setInput4Value(e.target.value)}
                        onKeyDown={handleFilterEnterKey}
                      />
                    </PremiumFieldWrap>
                  </div>
                )}

                {input5_type === "dropdown" &&
                  input5_multiple === "no" &&
                  input5 && (
                    <div className={premiumColClass(premium)}>
                      <PremiumFieldWrap
                        premium={premium}
                        label="Gender"
                        icon={<FaVenusMars size={12} />}
                        hasSelection={Boolean(input5Value)}
                      >
                        {renderPremiumSingleSelect(
                          input5Data,
                          input5Value,
                          setInput5Value,
                          "Search gender...",
                        )}
                      </PremiumFieldWrap>
                    </div>
                  )}

                {input10_type === "dropdown" &&
                  input10_multiple === "no" &&
                  input10 && (
                    <div className={premiumColClass(premium)}>
                      <PremiumFieldWrap
                        premium={premium}
                        label="Event"
                        icon={<FaCalendarAlt size={12} />}
                        hasSelection={Boolean(input10Value)}
                      >
                        {renderPremiumSingleSelect(
                          input10Data,
                          input10Value,
                          setInput10Value,
                          "Search events...",
                        )}
                      </PremiumFieldWrap>
                    </div>
                  )}

                {input11_type === "dropdown" &&
                  input11_multiple === "no" &&
                  input11 && (
                    <div className={premiumColClass(premium)}>
                      <PremiumFieldWrap
                        premium={premium}
                        label={getPremiumLabel(premiumLabels, "input11")}
                        icon={getPremiumIcon(premiumIcons, "input11")}
                        hasSelection={Boolean(input11Value)}
                      >
                        {renderPremiumSingleSelect(
                          input11Data,
                          input11Value,
                          setInput11Value,
                          "Search branches...",
                        )}
                      </PremiumFieldWrap>
                    </div>
                  )}

                {input11_type === "dropdown" &&
                  input11_multiple === "yes" &&
                  input11 && (
                    <div className={premiumColClass(premium)}>
                      <PremiumFieldWrap
                        premium={premium}
                        label={getPremiumLabel(premiumLabels, "input11")}
                        icon={getPremiumIcon(premiumIcons, "input11")}
                        hasSelection={Boolean(input11ArrayValue?.length)}
                      >
                        {renderPremiumMultiSelect(
                          input11_filter_by && input11_filter_by === "agencyname"
                            ? sortByAgencyName(input11Data)
                            : sortByTitle(input11Data),
                          input11ArrayValue,
                          setInput11ArrayValue,
                          `Search ${getPremiumLabel(premiumLabels, "input11").toLowerCase()}...`,
                        )}
                      </PremiumFieldWrap>
                    </div>
                  )}

                {input12_type === "dropdown" &&
                  input12_multiple === "yes" &&
                  input12 && (
                    <div className={premiumColClass(premium)}>
                      <PremiumFieldWrap
                        premium={premium}
                        label={getPremiumLabel(premiumLabels, "input12")}
                        icon={getPremiumIcon(premiumIcons, "input12")}
                        hasSelection={Boolean(input12ArrayValue?.length)}
                      >
                        {renderPremiumMultiSelect(
                          input12_filter_by && input12_filter_by === "name"
                            ? sortByName(input12Data)
                            : input12_filter_by === "agencyname"
                              ? sortByAgencyName(input12Data)
                              : sortByTitle(input12Data),
                          input12ArrayValue,
                          setInput12ArrayValue,
                          `Search ${getPremiumLabel(premiumLabels, "input12").toLowerCase()}...`,
                        )}
                      </PremiumFieldWrap>
                    </div>
                  )}

                {input14_type === "input" && input14 && (
                  <div className={premiumColClass(premium)}>
                    <PremiumFieldWrap
                      premium={premium}
                      label={getPremiumLabel(premiumLabels, "input14") || input14}
                      icon={getPremiumIcon(premiumIcons, "input14") || <FaSearch size={12} />}
                    >
                      <input
                        type="text"
                        className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-xs text-slate-800 outline-none"
                        value={input14Value}
                        placeholder={input14}
                        onChange={(e) => setInput14Value(e.target.value)}
                        onKeyDown={handleFilterEnterKey}
                      />
                    </PremiumFieldWrap>
                  </div>
                )}

                {input13_type === "dropdown" &&
                  input13_multiple === "yes" &&
                  input13 && (
                    <div className={premiumColClass(premium)}>
                      <PremiumFieldWrap
                        premium={premium}
                        label={getPremiumLabel(premiumLabels, "input13")}
                        icon={getPremiumIcon(premiumIcons, "input13")}
                        hasSelection={Boolean(input13ArrayValue?.length)}
                      >
                        {renderPremiumMultiSelect(
                          input13Data?.[0]?.country !== undefined
                            ? input13Data
                                .slice()
                                .map((item) => ({
                                  ...item,
                                  label: `${item.title} - ${item.country}`,
                                }))
                                .sort((a, b) =>
                                  (a.label || "").localeCompare(b.label || ""),
                                )
                            : sortByTitle(input13Data),
                          input13ArrayValue,
                          setInput13ArrayValue,
                          `Search ${getPremiumLabel(premiumLabels, "input13").toLowerCase()}...`,
                        )}
                      </PremiumFieldWrap>
                    </div>
                  )}
              </>
            )}

            {/* Input 10 fields */}
            {input10_type === "dropdown" &&
              input10_multiple === "yes" &&
              input10 &&
              !premium && (
                <div className="col-lg-2 col-6">
                  <Multiselect
                    options={
                      input10_filter_by && input10_filter_by === "name"
                        ? input10Data
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                        : input10Data
                            .slice()
                            .sort((a, b) => a.title.localeCompare(b.title))
                    }
                    className="form-select s-dropdown py-1 shadow-sm mt-1 chip-hide w-lg-90"
                    placeholder={input10}
                    displayValue={
                      input10_filter_by && input10_filter_by === "name"
                        ? "name"
                        : "title"
                    }
                    onSelect={(selected) => setInput10ArrayValue(selected)}
                    showCheckbox={true}
                    disablePreSelectedValues={true}
                    hidePlaceholder={false}
                    selectedValues={input10ArrayValue}
                  />
                </div>
              )}

            {input10_type === "dropdown" &&
              input10_multiple === "no" &&
              input10 &&
              !premium && (
                <div className="col-lg-2 col-6">
                  <select
                    value={input10}
                    onChange={(e) => setInput10Value(e.target.value)}
                    className="form-select s-dropdown pb-2 shadow-sm pt-1 w-lg-90 mt-1 form-control"
                  >
                    <option disabled>{input10}</option>
                    {input10Data.map((value) => (
                      <option
                        key={value.name}
                        className={input8 === value.name ? "selected" : ""}
                        value={value.name}
                      >
                        {value.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {input11_type === "dropdown" &&
              input11_multiple === "no" &&
              input11 &&
              !premium && (
                <div className="col-lg-2 col-6">
                  <select
                    value={input11}
                    onChange={(e) => setInput11Value(e.target.value)}
                    className="form-select s-dropdown shadow-sm pt-1 w-lg-90 mt-1 form-control"
                  >
                    <option disabled>{input11}</option>
                    {input11Data.map((value) => (
                      <option
                        key={value.title}
                        className={input11 === value.title ? "selected" : ""}
                        value={value.value}
                      >
                        {value.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {input11_type === "dropdown" &&
              input11_multiple === "yes" &&
              input11 &&
              !premium && (
                <div className="col-lg-2 col-6">
                  <Multiselect
                    options={
                      input11_filter_by && input11_filter_by === "agencyname"
                        ? input11Data
                            .slice()
                            .sort((a, b) =>
                              a.agencyname.localeCompare(b.agencyname),
                            )
                        : input11Data
                            .slice()
                            .sort((a, b) => a.title.localeCompare(b.title))
                    }
                    className="form-select s-dropdown py-1 shadow-sm mt-1 chip-hide w-lg-90"
                    placeholder={input11}
                    displayValue={
                      input11_filter_by && input11_filter_by === "agencyname"
                        ? "agencyname"
                        : "title"
                    }
                    onSelect={(selected) => setInput11ArrayValue(selected)}
                    showCheckbox={true}
                    disablePreSelectedValues={true}
                    hidePlaceholder={false}
                    selectedValues={input11ArrayValue}
                  />
                </div>
              )}

            {/* Input 12 fields */}
            {input12_type === "dropdown" &&
              input12_multiple === "yes" &&
              input12 &&
              !premium && (
                <div className="col-lg-2 col-6">
                  <Multiselect
                    options={
                      input12_filter_by && input12_filter_by === "agencyname"
                        ? input12Data
                            .slice()
                            .sort((a, b) =>
                              a.agencyname.localeCompare(b.agencyname),
                            )
                        : input12_filter_by === "name"
                          ? input12Data
                              .slice()
                              .sort((a, b) => a.name.localeCompare(b.name))
                          : input12Data
                              .slice()
                              .sort((a, b) => a.title.localeCompare(b.title))
                    }
                    className="form-select s-dropdown py-1 shadow-sm mt-1 chip-hide w-lg-90"
                    placeholder={input12}
                    displayValue={
                      input12_filter_by && input12_filter_by === "agencyname"
                        ? "agencyname"
                        : input12_filter_by === "name"
                          ? "name"
                          : "title"
                    }
                    onSelect={(selected) => setInput12ArrayValue(selected)}
                    showCheckbox={true}
                    disablePreSelectedValues={true}
                    hidePlaceholder={false}
                    selectedValues={input12ArrayValue}
                  />
                </div>
              )}

            {input13_type === "dropdown" &&
              input13_multiple === "no" &&
              input13 && (
                <div className="col-lg-2 col-6">
                  <select
                    value={input13}
                    onChange={(e) => setInput13Value(e.target.value)}
                    className="form-select s-dropdown shadow-sm pt-1 w-lg-90 mt-1 form-control"
                  >
                    <option disabled>{input13}</option>
                    {input13Data.map((value) => (
                      <option
                        key={value.title}
                        className={input13 === value.title ? "selected" : ""}
                        value={value.value ? value.value : value.title}
                      >
                        {value.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {/* Input 13 fields */}
            {input13_type === "dropdown" &&
              input13_multiple === "yes" &&
              input13 &&
              !premium && (
                <div className="col-lg-2 col-6">
                  <Multiselect
                    options={input13Data
                      .slice()
                      .map((item) => ({
                        ...item,
                        combinedText: `${item.title} - ${item.country}`,
                      }))
                      .sort((a, b) =>
                        a.combinedText.localeCompare(b.combinedText),
                      )}
                    className="form-select s-dropdown py-1 shadow-sm mt-1 chip-hide w-lg-90"
                    placeholder={input13}
                    displayValue={"combinedText"}
                    onSelect={(selected) => setInput13ArrayValue(selected)}
                    showCheckbox={true}
                    disablePreSelectedValues={true}
                    hidePlaceholder={false}
                    selectedValues={input13ArrayValue}
                  />
                </div>
              )}

            {input14_type === "input" && input14 && !premium && (
              <div className="col-lg-2 col-6">
                <input
                  type="text"
                  className="form-control search bg-white w-lg-90 m-1 shadow-sm"
                  value={input14Value}
                  placeholder={input14}
                  onChange={(e) => setInput14Value(e.target.value)}
                  onKeyDown={handleFilterEnterKey}
                />
              </div>
            )}

            {/* Search Button */}
            {!premium && (
            <div className="row mt-2">
              <div className="col-lg-10 col-12">
                <div className="row selectedValues">
                  {/* Input1 searched value */}
                  {input1Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput1Value("")}
                      ></button>
                      <div className="list-tag">{input1Value}</div>
                    </div>
                  )}
                  {/* Input1 searched value */}
                  {input1ArrayValue &&
                    input1ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle1InputChanges(index)}
                        ></button>
                        <div className="list-tag">
                          {input1_filter_by && input1_filter_by === "name"
                            ? item.name
                            : item.label}
                        </div>
                      </div>
                    ))}
                  {input2ArrayValue &&
                    input2ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle2InputChanges(index)}
                        ></button>
                        <div className="list-tag">
                          {input2_filter_by && input2_filter_by === "name"
                            ? item.name
                            : item.label}
                        </div>
                      </div>
                    ))}
                  {/* Input2 searched value */}
                  {input2Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput2Value("")}
                      ></button>
                      <div className="list-tag">{input2Value}</div>
                    </div>
                  )}
                  {/* Input3 searched value */}
                  {input3Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput3Value("")}
                      ></button>
                      <div className="list-tag">{input3Value}</div>
                    </div>
                  )}
                  {/* Input4 searched value */}
                  {input4Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput4Value("")}
                      ></button>
                      <div className="list-tag">{input4Value}</div>
                    </div>
                  )}
                  {input4ArrayValue &&
                    input4ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle4InputChanges(index)}
                        ></button>
                        <div className="list-tag">{item.name}</div>
                      </div>
                    ))}
                  {/* Input5 searched value */}
                  {input5Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput5Value("")}
                      ></button>
                      <div className="list-tag">{input5Value}</div>
                    </div>
                  )}
                  {/* Input6 searched value */}
                  {input6Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() =>
                          input6CallBack
                            ? sendValueDirectly()
                            : setInput6Value("")
                        }
                      ></button>
                      <div className="list-tag">{input6Value}</div>
                    </div>
                  )}
                  {input6ArrayValue &&
                    input6ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle6InputChanges(index)}
                        ></button>
                        <div className="list-tag">
                          {input6_filter_by && input6_filter_by === "agents"
                            ? item.agencyname
                            : item.title}
                        </div>
                      </div>
                    ))}
                  {/* Input7 searched value */}
                  {input7ArrayValue &&
                    input7ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle7InputChanges(index)}
                        ></button>
                        <div className="list-tag">
                          {input7_filter_by && input7_filter_by === "agents"
                            ? item.agencyname
                            : input7_suggestion
                              ? item.label
                              : item.name}
                        </div>
                      </div>
                    ))}
                  {input7Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput7Value("")}
                      ></button>
                      <div className="list-tag">{input7Value}</div>
                    </div>
                  )}
                  {/* Input8 searched value */}
                  {input8Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput8Value("")}
                      ></button>
                      <div className="list-tag">{input8Value}</div>
                    </div>
                  )}
                  {/* Input8 searched value */}
                  {input8ArrayValue &&
                    input8ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle8InputChanges(index)}
                        ></button>
                        <div className="list-tag">
                          {input8_filter_by && input8_filter_by === "name"
                            ? item.label
                              ? item.label
                              : item.name
                            : item.title}
                        </div>
                      </div>
                    ))}
                  {/* Input9 searched value */}
                  {input9ArrayValue &&
                    input9ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle9InputChanges(index)}
                        ></button>
                        <div className="list-tag">
                          {item.name
                            ? item.name
                            : item.label
                              ? item.label
                              : item.title}
                        </div>
                      </div>
                    ))}
                  {/* Input10 searched value */}
                  {input10ArrayValue &&
                    input10ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle10InputChanges(index)}
                        ></button>
                        <div className="list-tag">
                          {input8_filter_by && input8_filter_by === "name"
                            ? item.name
                            : item.title}
                        </div>
                      </div>
                    ))}
                  {input10Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput10Value("")}
                      ></button>
                      <div className="list-tag">{input10Value}</div>
                    </div>
                  )}
                  {/* Input11 searched value */}
                  {input11Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput11Value("")}
                      ></button>
                      <div className="list-tag">{input11Value}</div>
                    </div>
                  )}
                  {input11ArrayValue &&
                    input11ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle11InputChanges(index)}
                        ></button>
                        <div className="list-tag">
                          {input11_filter_by &&
                          input11_filter_by === "agencyname"
                            ? item.agencyname
                            : item.title}
                        </div>
                      </div>
                    ))}
                  {/* Input12 searched value */}
                  {input12ArrayValue &&
                    input12ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle12InputChanges(index)}
                        ></button>
                        <div className="list-tag">
                          {input12_filter_by &&
                          input12_filter_by === "agencyname"
                            ? item.agencyname
                            : input12_filter_by === "name"
                              ? item.name
                              : item.title}
                        </div>
                      </div>
                    ))}
                  {/* Input13 searched value */}
                  {input13Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput13Value("")}
                      ></button>
                      <div className="list-tag">{input13Value}</div>
                    </div>
                  )}

                  {/* Input13 searched value */}
                  {input13ArrayValue &&
                    input13ArrayValue.map((item, index) => (
                      <div key={index} className="tag-bg w-auto">
                        <button
                          type="button"
                          className="btn-close w-auto"
                          aria-label="Close"
                          onClick={() => handle13InputChanges(index)}
                        ></button>
                        <div className="list-tag">{item.title}</div>
                      </div>
                    ))}

                  {/* Input14 searched value */}
                  {input14Value && (
                    <div className="tag-bg w-auto">
                      <button
                        type="button"
                        className="btn-close w-auto"
                        aria-label="Close"
                        onClick={() => setInput14Value("")}
                      ></button>
                      <div className="list-tag">{input14Value}</div>
                    </div>
                  )}
                </div>
              </div>
              {/* Search Button */}
              {!hideSearchButton && (
              <div className={`col-lg-2 col-6${premium ? " flex items-end" : ""}`}>
                <button
                  type="button"
                  className={
                    premium
                      ? "inline-flex h-[38px] w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
                      : "btn btn-search float-right w-100 text-left"
                  }
                  onClick={(e) => searchBtn(e)}
                >
                  {premium ? (
                    <>
                      <FaSearch size={12} />
                      <span>Search</span>
                    </>
                  ) : (
                    <>
                      <i className="fa fa-search pr-2" aria-hidden="true"></i>
                      Search
                    </>
                  )}
                </button>
              </div>
              )}
            </div>
            )}
          </div>

          {premium && hideSearchButton && !hideFilterActionsRow && (
            <div className="cms-filter-actions-row">
              <button
                type="button"
                className="cms-filter-search-btn"
                onClick={searchBtn}
              >
                <FaSearch size={12} />
                <span>Search</span>
              </button>
              {showColumnsAfterSearch && allColumns && (
                <button
                  type="button"
                  className="cms-filter-columns-btn"
                  onClick={openColumnsSettings}
                  title="Column settings"
                  aria-label="Column settings"
                >
                  <FaCog size={13} aria-hidden />
                  <span>Columns</span>
                </button>
              )}
            </div>
          )}
        </form>
      </div>
      </PremiumDenseContext.Provider>
      {allColumns && (
        <CustomColumnsModel
          show={openCustomColumnsModel}
          parentCallback={handleModelCallback}
          allcolumns={allColumns}
          defaultDuplicate={duplicate}
          showDuplicateEntry={showDuplicateEntry}
        />
      )}
    </>
  );
};
export default MultipleSearchs;
